import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import FormInput from '../../components/ui/FormInput';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import { creatorService } from '../../services/creatorService';

const Onboarding: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    displayName: '',
    description: '',
    monthlyIncomeGoal: '',
    currency: 'USD',
    customUrl: '',
    primaryColor: '#FF6B6B',
    secondaryColor: '#4ECDC4',
    thirdColor: '#45B7D1',
    fontColor: '#FFFFFF',
    backgroundColor: '#1A1A1A',
    thankYouMessage: '',
    twitterHandle: '',
    instagramHandle: '',
    youtubeUrl: '',
    tiktokHandle: '',
    discordUrl: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const me = await creatorService.getMyCreatorProfile().catch(() => null);
        if (me) {
          setForm(prev => ({
            ...prev,
            displayName: me.displayName || '',
            description: me.description || '',
            monthlyIncomeGoal: me.monthlyIncomeGoal ? String(me.monthlyIncomeGoal) : '',
            currency: me.currency || 'USD',
            customUrl: me.customUrl || '',
            primaryColor: me.primaryColor || prev.primaryColor,
            secondaryColor: me.secondaryColor || prev.secondaryColor,
            thirdColor: me.thirdColor || prev.thirdColor,
            fontColor: me.fontColor || prev.fontColor,
            backgroundColor: me.backgroundColor || prev.backgroundColor,
            thankYouMessage: me.thankYouMessage || '',
            twitterHandle: me.twitterHandle || '',
            instagramHandle: me.instagramHandle || '',
            youtubeUrl: me.youtubeUrl || '',
            tiktokHandle: me.tiktokHandle || '',
            discordUrl: me.discordUrl || ''
          }));
        }
      } catch (e: any) {
        // non-blocking
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const update = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload: any = {
        ...form,
        monthlyIncomeGoal: form.monthlyIncomeGoal ? parseFloat(form.monthlyIncomeGoal) : undefined,
      };
      // Try update first; if fails, create
      try {
        await creatorService.updateCreatorProfile(payload);
      } catch {
        await creatorService.createCreatorProfile(payload);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading…</div>;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--patreon-cream)' }}>
      <Helmet>
        <title>Creator Onboarding | Patreon</title>
      </Helmet>
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-6" style={{ color: 'var(--patreon-black)' }}>Set up your creator page</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput label="Display name" value={form.displayName} onChange={e => update('displayName', e.target.value)} required />
          <Textarea label="Description" value={form.description} onChange={e => update('description', e.target.value)} rows={4} />

          <div className="grid sm:grid-cols-2 gap-4">
            <FormInput label="Monthly income goal (optional)" type="number" value={form.monthlyIncomeGoal} onChange={e => update('monthlyIncomeGoal', e.target.value)} />
            <Select label="Currency" value={form.currency} onChange={e => update('currency', e.target.value)}>
              {['USD','EUR','GBP','CAD','AUD','JPY'].map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </div>

          <FormInput label="Custom URL" value={form.customUrl} onChange={e => update('customUrl', e.target.value)} helperText="Lowercase, numbers and hyphens only" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <FormInput label="Primary color" type="color" value={form.primaryColor} onChange={e => update('primaryColor', e.target.value)} />
            <FormInput label="Secondary color" type="color" value={form.secondaryColor} onChange={e => update('secondaryColor', e.target.value)} />
            <FormInput label="Third color" type="color" value={form.thirdColor} onChange={e => update('thirdColor', e.target.value)} />
            <FormInput label="Font color" type="color" value={form.fontColor} onChange={e => update('fontColor', e.target.value)} />
            <FormInput label="Background color" type="color" value={form.backgroundColor} onChange={e => update('backgroundColor', e.target.value)} />
          </div>

          <Textarea label="Thank you message" value={form.thankYouMessage} onChange={e => update('thankYouMessage', e.target.value)} rows={3} />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormInput label="Twitter handle" value={form.twitterHandle} onChange={e => update('twitterHandle', e.target.value)} />
            <FormInput label="Instagram handle" value={form.instagramHandle} onChange={e => update('instagramHandle', e.target.value)} />
            <FormInput label="YouTube URL" value={form.youtubeUrl} onChange={e => update('youtubeUrl', e.target.value)} />
            <FormInput label="TikTok handle" value={form.tiktokHandle} onChange={e => update('tiktokHandle', e.target.value)} />
            <FormInput label="Discord URL" value={form.discordUrl} onChange={e => update('discordUrl', e.target.value)} />
          </div>

          {error && <div className="text-sm" style={{ color: 'var(--patreon-error)' }}>{error}</div>}

          <div className="flex justify-end gap-3">
            <Button variant="outline" type="button" onClick={() => window.history.back()} disabled={saving}>Cancel</Button>
            <Button type="submit" loading={saving}>Save</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Onboarding;



